import type {
  ChatAttachment,
  ChatAuthor,
  ChatItem,
  ChatPreview,
  ChatPreviewImage,
  ChatReaction,
  ClientRequestId,
  DateDivider,
  DeletedChatItem,
  FailedChatItem,
  FeedItemKey,
  MessageId,
  SendingChatItem,
  SentChatItem,
  SortOrder
} from './types';

/** Human-readable validation contract for branded message ids. */
export const MESSAGE_ID_DESCRIPTION = '8-character uppercase Crockford id';
/** Human-readable validation contract for branded sort orders. */
export const SORT_ORDER_DESCRIPTION = '26-character uppercase Crockford ULID';

const MESSAGE_ID_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;
const SORT_ORDER_PATTERN = /^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{26}$/;

const CHAT_ITEM_CONTENT_KEYS = [
  'id',
  'sortOrder',
  'itemKey',
  'clientRequestId',
  'author',
  'createdAt',
  'rendererRow',
  'body',
  'bodyHtml',
  'flags',
  'containsOnlyAttachments',
  'containsOnlyEntities',
  'containsOnlyEmojiEntities',
  'attachments',
  'previews',
  'reactions',
  'status',
  'deleted',
  'editedAt',
  'dateDivider',
  'groupedWithPrev'
] as const satisfies readonly (keyof ChatItem)[];
type ChatItemContentKey = (typeof CHAT_ITEM_CONTENT_KEYS)[number];
const chatItemContentKeysCoverEveryField: Record<
  Exclude<keyof ChatItem, ChatItemContentKey>,
  never
> = {};
void chatItemContentKeysCoverEveryField;

const CHAT_AUTHOR_CONTENT_KEYS = ['id', 'type', 'name', 'accent', 'avatarUrl'] as const satisfies readonly (keyof ChatAuthor)[];
type ChatAuthorContentKey = (typeof CHAT_AUTHOR_CONTENT_KEYS)[number];
const chatAuthorContentKeysCoverEveryField: Record<
  Exclude<keyof ChatAuthor, ChatAuthorContentKey>,
  never
> = {};
void chatAuthorContentKeysCoverEveryField;

const CHAT_ATTACHMENT_CONTENT_KEYS = [
  'id',
  'kind',
  'url',
  'srcSet',
  'width',
  'height',
  'thumbhash',
  'originalUrl',
  'fileName',
  'sizeBytes',
  'loadDelayMs'
] as const satisfies readonly (keyof ChatAttachment)[];
type ChatAttachmentContentKey = (typeof CHAT_ATTACHMENT_CONTENT_KEYS)[number];
const chatAttachmentContentKeysCoverEveryField: Record<
  Exclude<keyof ChatAttachment, ChatAttachmentContentKey>,
  never
> = {};
void chatAttachmentContentKeysCoverEveryField;

const CHAT_PREVIEW_IMAGE_CONTENT_KEYS = [
  'url',
  'width',
  'height',
  'thumbhash'
] as const satisfies readonly (keyof ChatPreviewImage)[];
type ChatPreviewImageContentKey = (typeof CHAT_PREVIEW_IMAGE_CONTENT_KEYS)[number];
const chatPreviewImageContentKeysCoverEveryField: Record<
  Exclude<keyof ChatPreviewImage, ChatPreviewImageContentKey>,
  never
> = {};
void chatPreviewImageContentKeysCoverEveryField;

const CHAT_PREVIEW_CONTENT_KEYS = [
  'url',
  'title',
  'description',
  'image'
] as const satisfies readonly (keyof ChatPreview)[];
type ChatPreviewContentKey = (typeof CHAT_PREVIEW_CONTENT_KEYS)[number];
const chatPreviewContentKeysCoverEveryField: Record<
  Exclude<keyof ChatPreview, ChatPreviewContentKey>,
  never
> = {};
void chatPreviewContentKeysCoverEveryField;

const CHAT_REACTION_CONTENT_KEYS = [
  'emoji',
  'count',
  'reactedByMe'
] as const satisfies readonly (keyof ChatReaction)[];
type ChatReactionContentKey = (typeof CHAT_REACTION_CONTENT_KEYS)[number];
const chatReactionContentKeysCoverEveryField: Record<
  Exclude<keyof ChatReaction, ChatReactionContentKey>,
  never
> = {};
void chatReactionContentKeysCoverEveryField;

const DATE_DIVIDER_CONTENT_KEYS = [
  'dateKey',
  'label',
  'viewerTimeZone'
] as const satisfies readonly (keyof DateDivider)[];
type DateDividerContentKey = (typeof DATE_DIVIDER_CONTENT_KEYS)[number];
const dateDividerContentKeysCoverEveryField: Record<
  Exclude<keyof DateDivider, DateDividerContentKey>,
  never
> = {};
void dateDividerContentKeysCoverEveryField;

interface ChatItemContentInput {
  readonly itemKey: FeedItemKey;
  readonly author: ChatAuthor;
  readonly createdAt: string;
  readonly rendererRow?: unknown;
  readonly body: string;
  readonly bodyHtml?: string;
  readonly flags?: string[];
  readonly containsOnlyAttachments?: boolean;
  readonly containsOnlyEntities?: boolean;
  readonly containsOnlyEmojiEntities?: boolean;
  readonly attachments: ChatAttachment[];
  readonly previews: ChatPreview[];
  readonly reactions: ChatReaction[];
  readonly dateDivider?: DateDivider;
  readonly groupedWithPrev?: boolean;
}

/** Constructor input for a server-backed visible row; flags are copied, child arrays are retained. */
export interface SentChatItemInput extends ChatItemContentInput {
  readonly id: MessageId;
  readonly sortOrder: SortOrder;
  readonly clientRequestId?: ClientRequestId;
  readonly deleted?: false;
  readonly editedAt?: string;
}

/** Constructor input for optimistic rows keyed by clientRequestId. */
export interface PendingChatItemInput extends ChatItemContentInput {
  readonly clientRequestId: ClientRequestId;
  readonly deleted?: false;
}

/** Constructor input for server-backed tombstones; body/attachments/previews/reactions are forced empty. */
export interface DeletedChatItemInput extends Omit<
  ChatItemContentInput,
  'body' | 'attachments' | 'previews' | 'reactions'
> {
  readonly id: MessageId;
  readonly sortOrder: SortOrder;
  readonly clientRequestId?: ClientRequestId;
  readonly editedAt?: string;
}

/** Server-backed row variants with durable id and sort order. */
export type ServerChatItem = SentChatItem | DeletedChatItem;

/** Validates and brands an opaque server message id (short URL-safe token), else returns undefined. */
export function messageIdFromString(value: string): MessageId | undefined {
  if (!MESSAGE_ID_PATTERN.test(value)) return undefined;
  // SAFETY: The pattern was validated immediately above, and this is the only constructor for the MessageId brand.
  return value as MessageId;
}

/** Validates and brands a 26-character uppercase Crockford ULID sort order, else returns undefined. */
export function sortOrderFromString(value: string): SortOrder | undefined {
  if (!SORT_ORDER_PATTERN.test(value)) return undefined;
  // SAFETY: The pattern was validated immediately above, and this is the only constructor for the SortOrder brand.
  return value as SortOrder;
}

/** Builds a sent row, copying flags but retaining attachment/preview/reaction array ownership. */
export function createSentChatItem(input: SentChatItemInput): SentChatItem {
  return {
    id: input.id,
    sortOrder: input.sortOrder,
    itemKey: input.itemKey,
    ...(input.clientRequestId === undefined ? {} : { clientRequestId: input.clientRequestId }),
    author: input.author,
    createdAt: input.createdAt,
    ...(input.rendererRow === undefined ? {} : { rendererRow: input.rendererRow }),
    body: input.body,
    ...(input.bodyHtml === undefined ? {} : { bodyHtml: input.bodyHtml }),
    ...(input.flags === undefined ? {} : { flags: [...input.flags] }),
    ...(input.containsOnlyAttachments === undefined
      ? {}
      : { containsOnlyAttachments: input.containsOnlyAttachments }),
    ...(input.containsOnlyEntities === undefined
      ? {}
      : { containsOnlyEntities: input.containsOnlyEntities }),
    ...(input.containsOnlyEmojiEntities === undefined
      ? {}
      : { containsOnlyEmojiEntities: input.containsOnlyEmojiEntities }),
    attachments: input.attachments,
    previews: input.previews,
    reactions: input.reactions,
    status: 'sent',
    ...(input.deleted === undefined ? {} : { deleted: input.deleted }),
    ...(input.editedAt === undefined ? {} : { editedAt: input.editedAt }),
    ...(input.dateDivider === undefined ? {} : { dateDivider: input.dateDivider }),
    ...(input.groupedWithPrev === undefined ? {} : { groupedWithPrev: input.groupedWithPrev })
  };
}

/** Builds a sending optimistic row without durable id/sort order. */
export function createSendingChatItem(input: PendingChatItemInput): SendingChatItem {
  return {
    itemKey: input.itemKey,
    clientRequestId: input.clientRequestId,
    author: input.author,
    createdAt: input.createdAt,
    ...(input.rendererRow === undefined ? {} : { rendererRow: input.rendererRow }),
    body: input.body,
    ...(input.bodyHtml === undefined ? {} : { bodyHtml: input.bodyHtml }),
    ...(input.flags === undefined ? {} : { flags: [...input.flags] }),
    ...(input.containsOnlyAttachments === undefined
      ? {}
      : { containsOnlyAttachments: input.containsOnlyAttachments }),
    ...(input.containsOnlyEntities === undefined
      ? {}
      : { containsOnlyEntities: input.containsOnlyEntities }),
    ...(input.containsOnlyEmojiEntities === undefined
      ? {}
      : { containsOnlyEmojiEntities: input.containsOnlyEmojiEntities }),
    attachments: input.attachments,
    previews: input.previews,
    reactions: input.reactions,
    status: 'sending',
    ...(input.deleted === undefined ? {} : { deleted: input.deleted }),
    ...(input.dateDivider === undefined ? {} : { dateDivider: input.dateDivider }),
    ...(input.groupedWithPrev === undefined ? {} : { groupedWithPrev: input.groupedWithPrev })
  };
}

/** Builds a failed optimistic row without durable id/sort order. */
export function createFailedChatItem(input: PendingChatItemInput): FailedChatItem {
  return {
    itemKey: input.itemKey,
    clientRequestId: input.clientRequestId,
    author: input.author,
    createdAt: input.createdAt,
    ...(input.rendererRow === undefined ? {} : { rendererRow: input.rendererRow }),
    body: input.body,
    ...(input.bodyHtml === undefined ? {} : { bodyHtml: input.bodyHtml }),
    ...(input.flags === undefined ? {} : { flags: [...input.flags] }),
    ...(input.containsOnlyAttachments === undefined
      ? {}
      : { containsOnlyAttachments: input.containsOnlyAttachments }),
    ...(input.containsOnlyEntities === undefined
      ? {}
      : { containsOnlyEntities: input.containsOnlyEntities }),
    ...(input.containsOnlyEmojiEntities === undefined
      ? {}
      : { containsOnlyEmojiEntities: input.containsOnlyEmojiEntities }),
    attachments: input.attachments,
    previews: input.previews,
    reactions: input.reactions,
    status: 'failed',
    ...(input.deleted === undefined ? {} : { deleted: input.deleted }),
    ...(input.dateDivider === undefined ? {} : { dateDivider: input.dateDivider }),
    ...(input.groupedWithPrev === undefined ? {} : { groupedWithPrev: input.groupedWithPrev })
  };
}

/** Builds a tombstone row with empty display collections and status sent. */
export function createDeletedChatItem(input: DeletedChatItemInput): DeletedChatItem {
  return {
    id: input.id,
    sortOrder: input.sortOrder,
    itemKey: input.itemKey,
    ...(input.clientRequestId === undefined ? {} : { clientRequestId: input.clientRequestId }),
    author: input.author,
    createdAt: input.createdAt,
    ...(input.rendererRow === undefined ? {} : { rendererRow: input.rendererRow }),
    ...(input.flags === undefined ? {} : { flags: [...input.flags] }),
    ...(input.containsOnlyAttachments === undefined
      ? {}
      : { containsOnlyAttachments: input.containsOnlyAttachments }),
    ...(input.containsOnlyEntities === undefined
      ? {}
      : { containsOnlyEntities: input.containsOnlyEntities }),
    ...(input.containsOnlyEmojiEntities === undefined
      ? {}
      : { containsOnlyEmojiEntities: input.containsOnlyEmojiEntities }),
    body: '',
    attachments: [],
    previews: [],
    reactions: [],
    status: 'sent',
    deleted: true,
    ...(input.editedAt === undefined ? {} : { editedAt: input.editedAt }),
    ...(input.dateDivider === undefined ? {} : { dateDivider: input.dateDivider }),
    ...(input.groupedWithPrev === undefined ? {} : { groupedWithPrev: input.groupedWithPrev })
  };
}

/** Narrows to rows that have durable server identity. */
export function isServerChatItem(item: ChatItem): item is ServerChatItem {
  return item.id !== undefined;
}

/** Narrows to server-backed tombstone rows. */
export function isDeletedChatItem(item: ChatItem): item is DeletedChatItem {
  return item.deleted === true;
}

/** Deep-compares render-relevant chat item content while ignoring object identity. */
export function contentEquals(left: ChatItem, right: ChatItem): boolean {
  return deepEqual(projectChatItemContent(left), projectChatItemContent(right));
}

function projectChatItemContent(item: ChatItem): Record<ChatItemContentKey, unknown> {
  return {
    id: item.id,
    sortOrder: item.sortOrder,
    itemKey: item.itemKey,
    clientRequestId: item.clientRequestId,
    author: projectChatAuthorContent(item.author),
    createdAt: item.createdAt,
    rendererRow: item.rendererRow,
    body: item.body,
    bodyHtml: item.bodyHtml,
    flags: item.flags,
    containsOnlyAttachments: item.containsOnlyAttachments,
    containsOnlyEntities: item.containsOnlyEntities,
    containsOnlyEmojiEntities: item.containsOnlyEmojiEntities,
    attachments: item.attachments.map(projectChatAttachmentContent),
    previews: item.previews.map(projectChatPreviewContent),
    reactions: item.reactions.map(projectChatReactionContent),
    status: item.status,
    deleted: item.deleted,
    editedAt: item.editedAt,
    dateDivider: item.dateDivider ? projectDateDividerContent(item.dateDivider) : undefined,
    groupedWithPrev: item.groupedWithPrev
  };
}

function projectChatAuthorContent(author: ChatAuthor): Record<ChatAuthorContentKey, unknown> {
  return {
    id: author.id,
    type: author.type,
    name: author.name,
    accent: author.accent,
    avatarUrl: author.avatarUrl
  };
}

function projectChatAttachmentContent(
  attachment: ChatAttachment
): Record<ChatAttachmentContentKey, unknown> {
  return {
    id: attachment.id,
    kind: attachment.kind,
    url: attachment.url,
    srcSet: attachment.srcSet,
    width: attachment.width,
    height: attachment.height,
    thumbhash: attachment.thumbhash,
    originalUrl: attachment.originalUrl,
    fileName: attachment.fileName,
    sizeBytes: attachment.sizeBytes,
    loadDelayMs: attachment.loadDelayMs
  };
}

function projectChatPreviewImageContent(
  image: ChatPreviewImage
): Record<ChatPreviewImageContentKey, unknown> {
  return {
    url: image.url,
    width: image.width,
    height: image.height,
    thumbhash: image.thumbhash
  };
}

function projectChatPreviewContent(preview: ChatPreview): Record<ChatPreviewContentKey, unknown> {
  return {
    url: preview.url,
    title: preview.title,
    description: preview.description,
    image: preview.image ? projectChatPreviewImageContent(preview.image) : undefined
  };
}

function projectChatReactionContent(
  reaction: ChatReaction
): Record<ChatReactionContentKey, unknown> {
  return {
    emoji: reaction.emoji,
    count: reaction.count,
    reactedByMe: reaction.reactedByMe
  };
}

function projectDateDividerContent(divider: DateDivider): Record<DateDividerContentKey, unknown> {
  return {
    dateKey: divider.dateKey,
    label: divider.label,
    viewerTimeZone: divider.viewerTimeZone
  };
}

function deepEqual(left: unknown, right: unknown): boolean {
  if (left === right) return true;

  if (typeof left !== 'object' || left === null || typeof right !== 'object' || right === null) {
    return false;
  }

  const leftIsArray = Array.isArray(left);
  const rightIsArray = Array.isArray(right);
  if (leftIsArray || rightIsArray) {
    if (!leftIsArray || !rightIsArray || left.length !== right.length) return false;
    return left.every((value, index) => deepEqual(value, right[index]));
  }

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every(
    (key) => Object.hasOwn(right, key) && deepEqual(Reflect.get(left, key), Reflect.get(right, key))
  );
}
